(ns ui.editor2.thumbnail-generator
  (:require [ui.editor2.extension-points :as extension-points]))

(defn create-video-thumbnail
  "Creates a thumbnail from a video URL by capturing a frame at the given time (in seconds)
   and resizing it to the given target width and height. The image is cropped to maintain
   the desired aspect ratio.

   Parameters:
   - video-src: URL of the video.
   - thumbnail-time: Time in seconds from which to capture the frame.
   - thumb-width: Desired thumbnail width.
   - thumb-height: Desired thumbnail height.

   Returns a Promise that resolves to a data URL of the thumbnail image."
  [{:keys [video-src thumbnail-time thumb-width thumb-height]}]
  (js/Promise.
   (fn [resolve reject]
     (let [video (js/document.createElement "video")]
       (set! (.-crossOrigin video) "anonymous")
       (set! (.-src video) video-src)
       ;; Once video data is loaded, jump to the frame specified by thumbnail-time.
       (.addEventListener video "loadeddata"
         (fn []
           (set! (.-currentTime video) thumbnail-time)))
       (.addEventListener video "seeked"
         (fn []
           (let [video-width (.-videoWidth video)
                 video-height (.-videoHeight video)
                 ;; Calculate the target (desired) aspect ratio.
                 desired-aspect (/ thumb-width thumb-height)
                 video-aspect (/ video-width video-height)
                 ;; Decide whether to crop horizontally or vertically.
                 [src-x src-y src-w src-h] (if (> video-aspect desired-aspect)
                                             ;; Video is wider than desired ratio; crop the sides.
                                             [( / (- video-width (* video-height desired-aspect)) 2)
                                              0
                                              (* video-height desired-aspect)
                                              video-height]
                                             ;; Video is taller than desired ratio; crop top and bottom.
                                             [0
                                              ( / (- video-height (/ video-width desired-aspect)) 2)
                                              video-width
                                              (/ video-width desired-aspect)])
                 ;; Create a canvas with the target thumbnail dimensions.
                 canvas (js/document.createElement "canvas")]
             (set! (.-width canvas) thumb-width)
             (set! (.-height canvas) thumb-height)
             (let [ctx (.getContext canvas "2d")]
               ;; Draw the cropped section of the video onto the canvas,
               ;; scaling it to the target thumbnail size.
               (.drawImage ctx video src-x src-y src-w src-h 0 0 thumb-width thumb-height)
               (resolve (.toDataURL canvas))))))
       (.addEventListener video "error"
         (fn [_e] (reject (js/Error. (str "Failed to load video for thumbnail: " video-src)))))))))

(comment
  (.then (create-video-thumbnail
           {:video-src "https://videos.pexels.com/video-files/1526909/1526909-hd_1280_720_24fps.mp4"
            :thumbnail-time 1
            :thumb-width 720
            :thumb-height 480})
         (fn [data-url]
           (prn data-url)))
  )

(defn create-video-thumbnail!
  [{:keys [store asset]}]
  (let [path [:editor2
              :video-thumbnails
              (:asset/uuid asset)]]
    (when-not (get-in @store
                      path)
      (-> (create-video-thumbnail
            {:video-src (:asset/uri asset)
             :thumbnail-time 1
             :thumb-width 720
             :thumb-height 480})
          (.then (fn [data-url]
                   (swap! store
                          assoc-in
                          path
                          data-url)))
          (.catch (fn [err]
                    (js/console.warn "Failed to generate video thumbnail for"
                                     (:asset/uuid asset) err)))))))

(defmethod extension-points/prepare-asset!
  :video
  [params]
  (create-video-thumbnail! params))
